import { HAZARD_CATEGORIES } from "encamp-shared/src/hazards/hazardCategories.generated";
import { HazardType } from "generated-graphql/graphql";

import { hazardCategoryStatement } from "./hazardCategoryStatements";
import { hazardClassDescription } from "./hazardClassDescriptions";

/** Header for single-category classes and selectable roots, which have no class of their own to sit under. */
export const STANDALONE_GROUP = "Other Classes";

export type HazardCategoryOption = {
  code: string;
  /** Full EPA label: what search matches and chips display. */
  label: string;
  /** Class header the option sits under; STANDALONE_GROUP for a standalone option. */
  group: string;
  /** Dropdown row text: the label without the class prefix. */
  optionLabel: string;
  /** Nesting below the class header; 0 for a class's direct categories. */
  depth: number;
  /** Code of the row directly above this one in the class tree; null for standalone options. */
  parentCode: string | null;
  /**
   * Label of a class-only node between this option and its class (e.g.
   * "Pyrophoric Gas" under Flammable Gases), rendered as a non-selectable
   * sub-header. Null when the option sits directly under the class or under
   * a selectable parent.
   */
  subgroup: string | null;
  /** Plain-language definition of the option's class. */
  description: string | null;
  /** The hazard statement an SDS prints for the category. */
  statement: string | null;
};

type SeedRow = (typeof HAZARD_CATEGORIES)[number];

const ROWS_BY_CODE = new Map<string, SeedRow>(HAZARD_CATEGORIES.map((row) => [row.code, row]));

/** Root class first, excluding the row itself. */
function ancestorsOf(row: SeedRow): SeedRow[] {
  const chain: SeedRow[] = [];
  let parentCode = row.parentCode;
  while (parentCode) {
    const parent = ROWS_BY_CODE.get(parentCode);
    if (!parent) break;
    chain.unshift(parent);
    parentCode = parent.parentCode;
  }
  return chain;
}

function stripPrefix(label: string, prefix: string): string {
  return label.startsWith(prefix) ? label.slice(prefix.length) : label;
}

function toOption(row: SeedRow, root: SeedRow, grouped: boolean): HazardCategoryOption {
  const explanation = {
    description: hazardClassDescription(root.code),
    statement: hazardCategoryStatement(row.code),
  };
  if (!grouped) {
    return {
      code: row.code,
      label: row.label,
      group: STANDALONE_GROUP,
      optionLabel: row.label,
      depth: 0,
      parentCode: null,
      subgroup: null,
      ...explanation,
    };
  }
  const ancestors = ancestorsOf(row);
  const parent = ancestors[ancestors.length - 1];
  // A class-only node below the class becomes a sub-header rather than a
  // prefix on every child.
  const subgroupNode = parent !== root && !parent.selectable ? parent : undefined;
  const prefixLabel = subgroupNode ? subgroupNode.label : root.label;
  return {
    code: row.code,
    label: row.label,
    group: root.label,
    optionLabel: stripPrefix(row.label, `${prefixLabel} – `),
    depth: ancestors.length - 1,
    parentCode: row.parentCode,
    subgroup: subgroupNode ? stripPrefix(subgroupNode.label, `${root.label} – `) : null,
    ...explanation,
  };
}

/**
 * The selectable categories of one hazard type as Autocomplete options. Seed
 * order is depth-first, so a class's options stay contiguous; standalone
 * options (single-category classes and selectable roots) are gathered under
 * STANDALONE_GROUP at the end so they never read as part of the last class.
 */
export function buildHazardCategoryOptions(hazardType: HazardType): HazardCategoryOption[] {
  const wanted: string = hazardType;
  const selectable = HAZARD_CATEGORIES.filter((row) => row.hazardType === wanted && row.selectable);

  const rootByCode = new Map<string, SeedRow>();
  const selectableCountByRoot = new Map<string, number>();
  for (const row of selectable) {
    const root = ancestorsOf(row)[0] ?? row;
    rootByCode.set(row.code, root);
    selectableCountByRoot.set(root.code, (selectableCountByRoot.get(root.code) ?? 0) + 1);
  }

  const options = selectable.map((row) => {
    const root = rootByCode.get(row.code) ?? row;
    const grouped = root.code !== row.code && (selectableCountByRoot.get(root.code) ?? 0) > 1;
    return toOption(row, root, grouped);
  });

  return [
    ...options.filter((option) => option.group !== STANDALONE_GROUP),
    ...options.filter((option) => option.group === STANDALONE_GROUP),
  ];
}
